/**
 * Web Components Registration
 * Import and register all web components (form inputs + panels/containers)
 */

// Form Input Components (extends FormElementBase)

import { FilterSimpleComponent } from './filters/filter-simple-component.js';

// Panel/Container Components
import { FullSearchPanel } from './full-search-panel.js';
import { FilterOperatorsComponent } from './filters/filter-operators-component.js';
import { FilterInputComponent } from './filters/filter-input-component.js';
import { FilterInputRangeComponent } from './filters/filter-input-range-component.js';
import { FilterSelectComponent } from './filters/filter-select-component.js';
import { AmbitoInputComponent } from './ambito-input/ambito-input-component.js';
import { ComuneInputComponent } from './ambito-input/comune-input.js';

import { FilterAmbitoComponent } from './filters/filter-ambito-component.js';
import { FullSearchComponent } from './full-search-component.js';
import { FrameLinkComponent } from './frames/frame-link-component.js';
import { FrameComponent } from './frames/frame-component.js';
import { DataCollector } from './data-components/data-collector.js';
import { AttributesComponent } from './attributes-input/attributes-component.js';
import { ParameterComponent } from './parameters-input/parameter-component.js';
import { FormComponent } from './form-component/form-component.js';
import { ParameterTypeComponent } from './parameter-type/parameter-type-component.js';
import { BonusParametersComponent } from './bonus-parameters/bonus-parameters.js';
import { HelpWindow } from './help-window/help-window.js';
import { ValueArrayInput } from './value-array-input/value-array-input.js';

import { HelpSystem } from '../logic/HelpSystem.js';
import UploadClientComponent from '@bonusmap/fileupload/client';
import { BonusFileComponent } from './bonus-file/bonus-file.js';
// [codex] zod-error component
import { ZodErrorComponent } from './zod-error/zod-error.js';
import { ParameterComponentInplace } from './parameters-input/parameter-component-inplace.js';
import { AttributesComponentInplace } from './attributes-input/attributes-component-inplace.js';
import { BonusParametersToggler } from './bonus-parameters/bonus-parameters-toggler.js';
import { BonusCategories } from './bonus-categories/bonus-categories.js';
import { FilterCategoriesComponent } from './filters/filter-categories-component.js';
import { ProfileAttributes } from './profile-attributes/profile-attributes.js';
import { ProfileAdditionalDataComponentInput } from './profile-data-input/profile-additional-data-component.js';
import { ProfileAdditionalDataItem } from './profile-data-input/profile-additional-data-item.js';
import { ProfileAdditionalDataItemList } from './profile-data-input/profile-additional-data-item-list.js';
import { registerViewers } from './viewers/index.js';
import { ProfileSelector } from './profile-selector/profile-selector.js';
import { BonusAddAi } from './bonus-add-ai/bonus-add-ai.js';
import { BonusEroganti } from './bonus-eroganti/bonus-eroganti.js';
import { FilterErogantiComponent } from './filters/filter-eroganti-component.js';
import { BonusViewParameters } from './bonus-parameters/bonus-view-parameters.js';
import { LoginComponent } from './login-component/login-component.js';
// [/codex]
/**
 * Register all web components as custom elements
*/
export function registerWebComponents(): void {
    if(window.webComponentsRegistered) return ; // Prevent double registration
    try {
        LoginComponent.registerElement(); // Register login component
        HelpWindow.registerElement(); // Register HelpWindow component
        UploadClientComponent.registerElement(); // Register file upload component
        BonusFileComponent.registerElement(); // Register file input component
        // Form component
        FormComponent.registerElement();
        // Frame components
        FrameLinkComponent.registerElement();
        FrameComponent.registerElement();

        // Register form input components
        FilterInputRangeComponent.registerElement();
        FilterOperatorsComponent.registerElement();
        FilterInputComponent.registerElement();

        AmbitoInputComponent.registerElement();
        ComuneInputComponent.registerElement();
        BonusParametersComponent.registerElement();
        BonusViewParameters.registerElement();
        BonusCategories.registerElement();
        BonusEroganti.registerElement();
        BonusParametersToggler.registerElement();
        ParameterComponent.registerElement();
        ParameterComponentInplace.registerElement();
        ParameterTypeComponent.registerElement();
        AttributesComponent.registerElement();
        AttributesComponentInplace.registerElement();
        ProfileAttributes.registerElement();
        ProfileAdditionalDataComponentInput.registerElement();
        ProfileAdditionalDataItem.registerElement();
        ProfileAdditionalDataItemList.registerElement();
        ValueArrayInput.registerElement();
        FilterSimpleComponent.registerElement();
        FilterSelectComponent.registerElement();
        FilterAmbitoComponent.registerElement();
        FilterCategoriesComponent.registerElement();
        FilterErogantiComponent.registerElement();
        FullSearchComponent.registerElement();
        // Register panel/container components
        FullSearchPanel.registerElement();
        DataCollector.registerElement();
        // [codex] register zod-error
        ZodErrorComponent.registerElement();
        BonusAddAi.registerElement();
        // [/codex]
        HelpSystem.register(); // Initialize HelpSystem to handle help links
        ProfileSelector.registerElement(); // Register ProfileSelector component
        registerViewers(); // Register viewers
    } catch (error) {
        console.error('Error registering web components:', error);
    }
}

// Export individual components for direct import if needed
export { 
FrameComponent,
    FrameLinkComponent,
    FilterInputRangeComponent,
    FilterInputComponent,
    FilterSelectComponent,
    AmbitoInputComponent,
    ComuneInputComponent,
    FilterAmbitoComponent,
    FilterSimpleComponent,
    FullSearchComponent,
    FilterOperatorsComponent,
    FullSearchPanel,
    ParameterTypeComponent
};
